import {
  type FiatPrice,
  Payment,
  Product,
  type QuotedPayment,
} from "@b2bportal/purchase-api";
import type { WalletOfferDiscountView } from "@b2bportal/wallet-offer-api";
import { getObjectKeysAsObject, setContextWithKey } from "@checkout/helpers";
import { ParentState } from "@checkout/types";
import { trackEvent } from "@hopper-b2b/api";
import { logger } from "@hopper-b2b/datadog";
import { LodgingShopTrackingEvents } from "@hopper-b2b/types";
import { assign, type ActionMeta, type EventObject } from "xstate";
import type {
  PriceChange,
  PriceChangeContext,
  PriceChangeTypeWithFiat,
} from "./types";

// The lodging shop deducts applicable wallet offers from the room's tripTotal,
// while the quote keeps them as separate TravelWalletOffer payments, so deduct
// them from the quoted total to compare like for like.
const subtractWalletOfferPayments = (
  total: FiatPrice,
  payments: QuotedPayment[] | undefined
): FiatPrice => {
  const offerAmounts = (payments ?? []).flatMap((p) =>
    p.payment.type === Payment.TravelWalletOffer
      ? [(p.payment.value as WalletOfferDiscountView).paymentAmount]
      : []
  );
  if (offerAmounts.some((amount) => amount.currency !== total.currencyCode)) {
    logger()?.error("PriceCheck: wallet offer currency mismatch", {
      payload: { total, offerAmounts },
    });
  }
  const discount = offerAmounts
    .filter((amount) => amount.currency === total.currencyCode)
    .reduce((sum, amount) => sum + Math.abs(amount.amount), 0);
  return { ...total, value: total.value - discount };
};

const getPriceChange = (context: PriceChangeContext): PriceChange => {
  const products = context.cartQuote.quoteBreakdown?.products;
  if (context.packageShop) {
    return {
      previousPrice:
        context.packageShop.selectedPackage?.pricing?.totalPrice?.fiat,
      newPrice: products?.find((p) => p.product.type === Product.Package)
        ?.product.value.totalPackagePricing?.tripTotal?.fiat as
        | FiatPrice
        | undefined,
    };
  }
  if (context.groundShop) {
    return {
      providerName:
        context.groundShop.selectedVehicle?.supplierName ??
        context.groundShop.selectedVehicle?.supplier?.name,
      previousPrice: context.groundShop.selectedVehicle?.totalCharge?.fiat,
      newPrice: products?.find((p) => p.product.type === Product.Ground)
        ?.product.value.vehAvail?.rentalRate?.totalCharge?.fiat as
        | FiatPrice
        | undefined,
    };
  }
  if (context.lodgingShop) {
    const shop = context.lodgingShop;
    const selectedRate = shop.selectedRoom?.products?.find(
      (p) => p.rateId?.value === shop.selectedRoomRateId
    );
    const quotedTripTotal = products?.find(
      (p) => p.product.type === Product.Hotel
    )?.product.value.pricingV1?.tripTotal?.fiat as FiatPrice | undefined;
    return {
      providerName: selectedRate?.providerName,
      lodgingName: shop.selectedLodging?.lodging?.name,
      previousPrice: selectedRate?.tripTotal?.fiat,
      newPrice:
        quotedTripTotal &&
        subtractWalletOfferPayments(
          quotedTripTotal,
          context.cartQuote.quoteBreakdown?.payments
        ),
    };
  }
  return {
    previousPrice: context.common?.tripPricing?.totalPricing?.total?.fiat,
    newPrice: products?.find(
      (p) =>
        p.product.type === Product.Flight ||
        p.product.type === Product.MultiProviderFlight
    )?.product.value.pricingSummary?.totalPricing?.total?.fiat as
      | FiatPrice
      | undefined,
  };
};

export const actions = {
  trackPriceChange: (
    context: PriceChangeContext,
    _event: EventObject,
    meta: ActionMeta<
      PriceChangeContext,
      EventObject,
      { type: string; screen: string }
    >
  ) => {
    const priceChange = context.cartQuote.priceChange;
    if (!priceChange?.hasDifference || priceChange.acknowledged) {
      return;
    }

    const prices = getPriceChange(context);
    if (prices.previousPrice == null || prices.newPrice == null) {
      return;
    }
    const difference = prices.newPrice.value - prices.previousPrice.value;

    trackEvent({
      eventName: LodgingShopTrackingEvents.hotel_viewed_price_change_snackbar,
      properties: {
        screen: meta.action.screen,
        price_change_type: difference > 0 ? "increased" : "decreased",
        provider_name: prices.providerName ?? "",
        lodging_name: prices.lodgingName ?? "",
        price_change_percentage:
          prices.previousPrice.value > 0
            ? Math.round((difference / prices.previousPrice.value) * 100)
            : 0,
        price_change_amount: difference,
        price_change_amount_currency: prices.newPrice.currencyCode,
      },
    });
  },
  checkForPriceChange: assign((ctx: PriceChangeContext, _event: unknown) => {
    if (ctx.cartQuote?.priceChange?.acknowledged) {
      return ctx;
    }

    const threshold = ctx.checkoutPriceChangeThreshold ?? 1;
    const prices = getPriceChange(ctx);
    if (
      prices == null ||
      prices.newPrice == null ||
      prices.previousPrice == null
    ) {
      return ctx;
    }

    if (prices.newPrice.currencyCode !== prices.previousPrice.currencyCode) {
      logger()?.error("PriceCheck: currency mismatch", { payload: prices });
    }

    const diff = prices.newPrice.value - prices.previousPrice.value;
    const priceChange: PriceChangeTypeWithFiat = {
      hasDifference: Math.abs(diff) > threshold,
      predictedTotal: prices.previousPrice.value,
      predictedTotalFiat: prices.previousPrice,
      priceQuoteTotal: prices.newPrice.value,
      priceQuoteTotalFiat: prices.newPrice,
      difference: diff,
      isIncrease: diff > 0,
      acknowledged: false,
    };

    return setContextWithKey(
      ctx,
      `${ParentState.cartQuote}.priceChange`,
      priceChange
    );
  }),
  resetAcknowledgePriceChange: assign(
    (ctx: PriceChangeContext, _event: unknown) =>
      setContextWithKey(ctx, `${ParentState.cartQuote}.priceChange`, undefined)
  ),
};

export const ActionTypes = getObjectKeysAsObject(actions);
